import { useEffect, useMemo } from 'react';

import AwsS3 from '@uppy/aws-s3';
import type { Body, Meta, Uppy as UppyType } from '@uppy/core';
import Uppy from '@uppy/core';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type { ImageLocation } from '@change-corgi/middle-end-gql-schema';

import { getPhotoUploadCredentials } from 'src/app/shared/hooks/uppyUploader';
import { genAWSUploadId } from 'src/app/shared/utils/upload';

export function useUppy({
	maxNumberOfFiles,
	maxFileSize,
	imageLocation,
	autoProceed = false,
}: {
	maxNumberOfFiles: number;
	maxFileSize: number;
	imageLocation: ImageLocation;
	autoProceed?: boolean;
}): ModelHookResult<
	{
		uppy: UppyType<Meta, Body>;
	},
	undefined
> {
	const utilityContext = useUtilityContext();
	const uppy = useMemo(
		() =>
			new Uppy<Meta, Body>({
				autoProceed,
				restrictions: {
					maxNumberOfFiles,
					allowedFileTypes: ['image/png', 'image/gif', 'image/jpeg'],
					maxFileSize,
				},
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	useEffect(() => {
		uppy.use(AwsS3, {
			id: genAWSUploadId(),
			shouldUseMultipart: false,
			getUploadParameters: async (file) => {
				const uploadCredentials = await getPhotoUploadCredentials(file.name, utilityContext, imageLocation);
				return {
					method: 'POST',
					url: uploadCredentials?.postUrl ?? '',
					fields: (uploadCredentials?.s3formData ?? {}) as Record<string, string>,
				};
			},
		});
	}, [imageLocation, uppy, utilityContext]);

	return {
		data: {
			uppy,
		},
	};
}
